import React, { useEffect, useState } from "react";
import {
    Avatar,
    Badge,
    Card,
    Col,
    Descriptions,
    Row,
    Space,
    Tag,
    Typography,
} from "antd";
import {
    BgColorsOutlined,
    HeartFilled,
    LockOutlined,
    MobileOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";

import AboutAPI from "../api/about";
import DashboardSkeleton from "./DashboardSkeleton";

const { Title, Paragraph, Text, Link } = Typography;

const FEATURES = [
    {
        icon: <LockOutlined />,
        title: "Single Sign-On",
        desc: "One IT account for every Chula service.",
    },
    {
        icon: <SafetyCertificateOutlined />,
        title: "Two-Factor Auth",
        desc: "TOTP with backup codes for stronger account protection.",
    },
    {
        icon: <BgColorsOutlined />,
        title: "Themes",
        desc: "Light, dark, and faculty palettes — synced across devices.",
    },
    {
        icon: <MobileOutlined />,
        title: "Responsive",
        desc: "Works on desktop and mobile out of the box.",
    },
];

const CREDITS = [
    { name: "React", url: "https://react.dev/" },
    { name: "Ant Design", url: "https://ant.design/" },
    { name: "FastAPI", url: "https://fastapi.tiangolo.com/" },
    { name: "pyotp", url: "https://pyauth.github.io/pyotp/" },
    { name: "Inter", url: "https://rsms.me/inter/" },
    { name: "TH Baijam (SIPA)", url: "https://www.f0nt.com/" },
];

export default function About() {
    const [info, setInfo] = useState(null);

    useEffect(() => {
        AboutAPI.get(
            (res) => setInfo(res),
            (err) => console.log("about fetch failed", err),
        );
    }, []);

    if (!info) return <DashboardSkeleton />;

    const isReady = info.status === "ready";

    return (
        <>
            <Card>
                <Space
                    direction="vertical"
                    size={20}
                    style={{ width: "100%", alignItems: "center", textAlign: "center" }}
                >
                    <img
                        src="/html/logo-chula-color.svg"
                        height={72}
                        alt="Chula"
                        style={{ display: "block" }}
                    />
                    <div>
                        <Title level={2} style={{ marginBottom: 8 }}>
                            {info.AppName}
                        </Title>
                        <Space size={6} wrap style={{ justifyContent: "center" }}>
                            <Tag color="magenta">v{info.AppVer}</Tag>
                            <Tag>codename: {info.CodeName}</Tag>
                            <Badge
                                status={isReady ? "success" : "warning"}
                                text={info.status}
                            />
                        </Space>
                    </div>
                    <Paragraph
                        type="secondary"
                        style={{ maxWidth: 560, margin: 0 }}
                    >
                        {info.Description}
                    </Paragraph>
                </Space>
            </Card>

            <Card title="What's included">
                <Row gutter={[24, 20]}>
                    {FEATURES.map((f) => (
                        <Col xs={24} sm={12} key={f.title}>
                            <Space size={14} align="start">
                                <Avatar
                                    size={44}
                                    shape="square"
                                    icon={f.icon}
                                    style={{
                                        flexShrink: 0,
                                        background: "var(--ant-color-fill-tertiary, rgba(0, 0, 0, 0.04))",
                                        color: "var(--ant-color-primary)",
                                    }}
                                />
                                <div>
                                    <Text strong>{f.title}</Text>
                                    <br />
                                    <Text type="secondary">{f.desc}</Text>
                                </div>
                            </Space>
                        </Col>
                    ))}
                </Row>
            </Card>

            <Card title="System">
                <Descriptions
                    column={{ xs: 1, sm: 2 }}
                    size="small"
                    colon={false}
                >
                    <Descriptions.Item label="App name">
                        {info.AppName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Version">
                        {info.AppVer}
                    </Descriptions.Item>
                    <Descriptions.Item label="Code name">
                        {info.CodeName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Build">
                        <Text code copyable={{ text: info.Build }}>
                            {info.Build}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Badge
                            status={isReady ? "success" : "warning"}
                            text={info.status}
                        />
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <Card title="Built with">
                <Space size={[8, 8]} wrap>
                    {CREDITS.map((c) => (
                        <Tag key={c.name}>
                            <Link href={c.url} target="_blank" rel="noreferrer">
                                {c.name}
                            </Link>
                        </Tag>
                    ))}
                </Space>
                <Paragraph
                    style={{ marginTop: 18, marginBottom: 0 }}
                    type="secondary"
                >
                    Made with{" "}
                    <HeartFilled style={{ color: "var(--ant-color-primary)" }} />{" "}
                    at Chulalongkorn University by{" "}
                    <Text strong>Krerk Piromsopa, Ph.D.</Text>
                </Paragraph>
            </Card>
        </>
    );
}
